/** @jsxRuntime automatic */
/** @jsxImportSource react */
import type { Bloque, Contenido } from "@gestiondoc/shared";
import { densidadDe } from "@gestiondoc/shared";
import { enLinea, resaltadosDe } from "./enLinea.js";
import type { DatosDeHoja } from "./HojaISO.js";

/** Los bloques del cuerpo de la hoja: cada nodo del contenido como se imprime en SMG. */

export function BloqueDeHoja({ bloque, datos }: { bloque: Bloque; datos: DatosDeHoja }) {
  const id = bloque.attrs.id;
  switch (bloque.type) {
    case "paragraph":
      return <p data-bloque={id}>{enLinea(bloque.content, resaltadosDe(datos.resaltados, id))}</p>;
    case "heading":
      // Los subtítulos se numeran solos (5.1, 5.2, 5.2.1) con contadores CSS, como en SMG.
      return bloque.attrs.level === 2 ? (
        <h2 data-bloque={id}>{enLinea(bloque.content, resaltadosDe(datos.resaltados, id))}</h2>
      ) : (
        <h3 data-bloque={id}>{enLinea(bloque.content, resaltadosDe(datos.resaltados, id))}</h3>
      );
    case "bulletList":
      return (
        <ul data-bloque={id}>
          {bloque.content.map((item) => (
            <li key={item.attrs.id} data-bloque={item.attrs.id}>
              {item.content.map((p) => (
                <Parrafo key={p.attrs.id} parrafo={p} datos={datos} />
              ))}
            </li>
          ))}
        </ul>
      );
    case "pasos":
      return (
        <table className="hoja-pasos" data-bloque={id}>
          <thead>
            <tr>
              <th>#</th>
              <th>Paso</th>
              <th>Responsable</th>
            </tr>
          </thead>
          <tbody>
            {bloque.content.map((paso) => (
              <tr key={paso.attrs.id} data-bloque={paso.attrs.id}>
                <td className="hoja-paso-n" />
                <td>
                  {paso.content.map((p) => (
                    <Parrafo key={p.attrs.id} parrafo={p} datos={datos} />
                  ))}
                </td>
                <td className="hoja-paso-quien">{paso.attrs.responsable}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    case "table": {
      const clase = bloque.attrs.variante
        ? { firmas: "hoja-tabla-firmas", sinBordes: "hoja-tabla-sin-bordes" }[bloque.attrs.variante]
        : undefined;
      return (
        <table
          data-bloque={id}
          className={clase}
          style={anchoDeTabla(bloque, datos.contenido) ?? undefined}
        >
          <tbody>
            {bloque.content.map((fila) => (
              <tr key={fila.attrs.id} data-bloque={fila.attrs.id}>
                {fila.content.map((celda) => {
                  const Celda = celda.type === "tableHeader" ? "th" : "td";
                  // El ancho de TipTap (px de la hoja), si lo trae: los formatos del seed lo usan
                  // para que una columna de nombres no quede tan angosta como una de casillas.
                  const ancho = celda.attrs.colspan === 1 ? celda.attrs.colwidth?.[0] : undefined;
                  return (
                    <Celda
                      key={celda.attrs.id}
                      data-bloque={celda.attrs.id}
                      colSpan={celda.attrs.colspan > 1 ? celda.attrs.colspan : undefined}
                      rowSpan={celda.attrs.rowspan > 1 ? celda.attrs.rowspan : undefined}
                      style={ancho ? { width: `${ancho}px` } : undefined}
                    >
                      {celda.content.map((p) => (
                        <Parrafo key={p.attrs.id} parrafo={p} datos={datos} />
                      ))}
                    </Celda>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    case "imagen":
      return (
        <figure className="hoja-figura" data-bloque={id}>
          <img
            src={datos.urlDeArchivo(bloque.attrs.archivoId)}
            alt={bloque.attrs.alt}
            style={{ width: `${bloque.attrs.ancho}%` }}
          />
          {bloque.attrs.alt && <figcaption>{bloque.attrs.alt}</figcaption>}
        </figure>
      );
    case "flujograma": {
      const svg = datos.svgs?.[id];
      // El SVG lo produce Mermaid a partir del código guardado, con su `securityLevel` estricto,
      // que escapa los textos: nadie mete HTML a mano en `svgs`.
      return svg ? (
        <div
          className="hoja-flujograma"
          data-bloque={id}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: SVG generado por Mermaid en modo estricto, no texto de una persona
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      ) : (
        <div className="hoja-flujograma-pendiente" data-bloque={id}>
          Flujograma
        </div>
      );
    }
    case "listaDeVerificacion":
      return <CuadriculaDeVerificacion id={id} areas={bloque.attrs.areas} />;
    case "nota":
      return (
        <aside className="hoja-nota" data-bloque={id} data-tono={bloque.attrs.tono}>
          <span className="hoja-nota-etiqueta">
            {bloque.attrs.tono === "advertencia" ? "Advertencia" : "Nota"}
          </span>
          {bloque.content.map((p) => (
            <Parrafo key={p.attrs.id} parrafo={p} datos={datos} />
          ))}
        </aside>
      );
  }
}

/** Un párrafo dentro de otro bloque (punto de lista, paso, celda, nota), con su id y sus marcas. */
function Parrafo({
  parrafo,
  datos,
}: {
  parrafo: Extract<Bloque, { type: "paragraph" }>;
  datos: DatosDeHoja;
}) {
  return (
    <p data-bloque={parrafo.attrs.id}>
      {enLinea(parrafo.content, resaltadosDe(datos.resaltados, parrafo.attrs.id))}
    </p>
  );
}

/** Puntos por fila en la cuadrícula de verificación, como en el formato de papel. */
const COLUMNAS_DE_VERIFICACION = 7;

/**
 * La lista de verificación como la imprime SMG (GES-REG-022): una tabla con el área a la
 * izquierda y sus puntos en filas de siete; debajo de cada fila de nombres, una fila en blanco
 * con una casilla por punto, donde se marca a mano (N/A, aceptable, no aceptable).
 */
function CuadriculaDeVerificacion({
  id,
  areas,
}: {
  id: string;
  areas: { nombre: string; puntos: string[] }[];
}) {
  const n = COLUMNAS_DE_VERIFICACION;
  return (
    <table className="hoja-verificacion" data-bloque={id}>
      <colgroup>
        <col className="hoja-verificacion-col-area" />
        {Array.from({ length: n }, (_, c) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: columnas fijas
          <col key={c} />
        ))}
      </colgroup>
      {areas.map((a, ai) => {
        const filas: (string | null)[][] = [];
        for (let k = 0; k < a.puntos.length; k += n) {
          const fila: (string | null)[] = a.puntos.slice(k, k + n);
          while (fila.length < n) fila.push(null);
          filas.push(fila);
        }
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: las áreas y los puntos son posicionales por diseño
          <tbody key={ai} className="hoja-verificacion-area">
            {filas.flatMap((fila, fi) => [
              // biome-ignore lint/suspicious/noArrayIndexKey: filas posicionales
              <tr key={`n${fi}`}>
                {fi === 0 && (
                  <th rowSpan={filas.length * 2} className="hoja-verificacion-nombre">
                    {a.nombre}
                  </th>
                )}
                {fila.map((p, c) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: ídem
                  <td key={c}>{p ?? ""}</td>
                ))}
              </tr>,
              // biome-ignore lint/suspicious/noArrayIndexKey: filas posicionales
              <tr key={`m${fi}`} className="hoja-verificacion-marcas">
                {fila.map((_, c) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: ídem
                  <td key={c} />
                ))}
              </tr>,
            ])}
          </tbody>
        );
      })}
    </table>
  );
}

/**
 * En la hoja compacta una tabla mide lo que suman sus columnas, como en Excel: la de criterios
 * no va de borde a borde ni la de verificación en el sitio ocupa todo el ancho. Se toma la
 * primera fila en que todas las celdas traen su ancho (y no abarcan columnas); si no hay, la
 * tabla va al ancho de la hoja, como siempre. En la hoja normal nada cambia.
 */
function anchoDeTabla(
  tabla: Extract<Bloque, { type: "table" }>,
  contenido: Contenido,
): { width: string } | null {
  if (densidadDe(contenido) !== "compacta") return null;
  for (const fila of tabla.content) {
    const anchos = fila.content.map((c) =>
      c.attrs.colspan === 1 && c.attrs.colwidth?.[0] ? c.attrs.colwidth[0] : 0,
    );
    if (anchos.every((a) => a > 0)) return { width: `${anchos.reduce((s, a) => s + a, 0)}px` };
  }
  return null;
}
